import { memo } from 'react'

/**
 * The deck's back, drawn.
 *
 * The bundled `cards_back.jpg` is pale-blue line art — good drawing, but a
 * cold blue against a warm gold-on-indigo app, and a 198 KB raster shown at
 * 180px. This is the same engraved-line language as the landing page's
 * astrolabe: concentric rules, a tick ring whose dash pattern walks the
 * circumference, and a star at the centre.
 *
 * It scales with the card, so one component serves the deck, the fan and the
 * spread without a second asset.
 *
 * It is drawn once, as an image, and every card shows that image. A reading
 * has well over a hundred backs on the page (the deck, the fan's 78), and
 * as inline SVG each was some twenty elements to build, style and paint.
 *
 * In order: the ground; the frame; the rosette, with its eight-pointed star
 * made of two squares, one turned; the crescents above and below, the way a
 * plate marks its poles; the corner diamonds.
 */
const DRAWING = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 180 310" preserveAspectRatio="none">
  <defs>
    <linearGradient id="ground" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#211a45"/>
      <stop offset="55%" stop-color="#140f2c"/>
      <stop offset="100%" stop-color="#0a0718"/>
    </linearGradient>
  </defs>
  <rect width="180" height="310" fill="url(#ground)"/>
  <g fill="none" stroke="#c9a244" stroke-linecap="round">
    <rect x="7" y="7" width="166" height="296" rx="5" stroke-width="1.4"/>
    <rect x="14" y="14" width="152" height="282" rx="3" stroke-width="0.7" opacity="0.6"/>
    <g transform="translate(90 155)">
      <circle r="54" stroke-width="0.7" opacity="0.55"/>
      <circle r="46" stroke-width="4" stroke-dasharray="0.8 6.42" opacity="0.9"/>
      <circle r="38" stroke-width="1.1"/>
      <circle r="17" stroke-width="0.7" opacity="0.7"/>
      <rect x="-19" y="-19" width="38" height="38" stroke-width="1.1"/>
      <rect x="-19" y="-19" width="38" height="38" stroke-width="1.1" transform="rotate(45)"/>
      <circle r="4.5" stroke-width="1.1" fill="#e8c56d"/>
    </g>
    <path d="M90 52a17 17 0 1 0 0 34 13 13 0 1 1 0-34Z" stroke-width="1" opacity="0.85"/>
    <path d="M90 258a17 17 0 1 1 0-34 13 13 0 1 0 0 34Z" stroke-width="1" opacity="0.85"/>
    <g stroke-width="0.9" opacity="0.7">
      <rect x="24" y="24" width="9" height="9" transform="rotate(45 28.5 28.5)"/>
      <rect x="147" y="24" width="9" height="9" transform="rotate(45 151.5 28.5)"/>
      <rect x="24" y="277" width="9" height="9" transform="rotate(45 28.5 281.5)"/>
      <rect x="147" y="277" width="9" height="9" transform="rotate(45 151.5 281.5)"/>
    </g>
  </g>
</svg>`

const SRC = `data:image/svg+xml,${encodeURIComponent(DRAWING.replace(/\n\s*/g, ''))}`

const CardBack = ({ className = '' }) => (
    <img
        className={`card-back ${className}`.trim()}
        src={SRC}
        alt=""
        aria-hidden="true"
        draggable={false}
    />
)

export default memo(CardBack)
